import { useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Building2,
  BadgeCheck,
  CircleSlash,
  MapPin,
  ExternalLink,
  Briefcase,
} from "lucide-react";
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../component/ui/loader/inner-loader";
import { Alert, Snackbar } from "@mui/material";
import { GlobalContext } from "../../../../context/Context";
import Chip from "../../../../component/ui/chip";
import DataNotFound from "../../../../component/ui/loader/data-not-found";
import FuleInvPopup from "../popup";
import UpdateInvoice from "../popup/update-invoice";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Badge({ children, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
  };
  return (
    <span className={classNames("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium", tones[tone])}>
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}

function Stat({ title, value, icon: Icon, hint, tone = "default", valueColor = "" }) {
  return (
    <div className="group relative rounded-2xl border border-solid border-slate-200 bg-white p-4 shadow-sm hover:shadow-md">
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <div className="text-xs text-slate-500">{title}</div>
          <div className={`text-lg font-semibold break-all !text-[16px] tracking-tight capitalize ${valueColor}`}>{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

export default function InvoiceQuickView({ open = true, onClose = () => { }, data, onReload = () => { }, invoiceId }) {

  let { state } = useContext(GlobalContext);

  const containerRef = useRef(null);
  const apiCall = useApiCall();
  const [isLoading, setIsLoading] = useState(false);
  const [details, setDetails] = useState({});
  const [alertData, setAlertData] = useState({ isShow: false });

  const [showModal, setShowModal] = useState(false)

  const handleClose = () => {
    setAlertData({ isShow: false })
  }

  const getData = async () => {
    setIsLoading(false)
    let apiData = `get-water-tanker-invoice-detail?id=${invoiceId}`;

    try {
      let res = await apiCall({ method: 'get', url: apiData });
      setDetails(res.data);
      setIsLoading(true);
    } catch (error) {
      console.warn("Error : ", error)
    }
  }

  // useOnClickOutside(containerRef, () => onClose());

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const el = document.getElementById("quickview-search");
        el?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    getData();
  }, [data]);

  const onSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message })
    setShowModal(false);
    getData();
    onReload();
  }

  const onError = (message) => {
    setAlertData({ isShow: true, type: "error", message: message })
  }

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.aside
              ref={containerRef}
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              className="relative mt-auto mr-[2vh] mb-auto ml-0 flex h-[96vh] w-[1000px] max-w-[98vw] flex-col overflow-hidden rounded-3xl border border-solid border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-xl"
              aria-modal
              role="dialog"
            >
              {
                isLoading ? (
                  <>
                    {/* Header */}
                    <div className="border-b border-solid border-slate-200 bg-white/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full relative z-30">
                      <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                        <div className="flex min-w-0 items-center gap-3">
                          {
                            details?.user_data?.employee_profile_picture ?

                              <img
                                src={details?.user_data?.employee_profile_picture}
                                alt={details?.user_data?.employee_name}
                                className="size-12 rounded-2xl object-cover ring-2 ring-white shadow"
                              />
                              :
                              <div className="size-12 shrink-0 rounded-full bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 grid place-items-center text-sm font-medium">{details?.user_data?.employee_name?.split(" ")?.[0]?.charAt(0)}{details?.user_data?.employee_name?.split(" ")?.[1]?.charAt(0)}</div>
                          }
                          <div className="min-w-0">
                            <div className="flex-col">
                              <div className="flex gap-2 items-center">
                                <h2 className="truncate text-lg font-semibold tracking-tight text-slate-900">
                                  {details?.vendor_name}
                                </h2>
                                {/* <Badge tone={details?.status == 1 ? "green" : "red"} icon={details?.status == 1 ? BadgeCheck : CircleSlash}></Badge> */}
                              </div>
                              {/* <div className="flex gap-2 items-center">
                                <Badge tone="blue" icon={Briefcase}>{details?.vendor_details?.vendor_company_name}</Badge>
                              </div> */}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {details?.status == "unpaid" ?
                            <div
                              className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                              onClick={() => {setShowModal(true)}}
                            >
                              Update Status
                            </div>
                            :
                            null
                          }
                          <button
                            className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                            onClick={onClose}
                            aria-label="Close"
                          >
                            <X className="size-4" /> Exit
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="grid min-h-0 flex-1 grid-cols-12 gap-4 w-full overflow-hidden p-4">
                      {/* Left: main content */}
                      <div className="col-span-12 flex min-h-0 flex-col w-full gap-4 lg:col-span-12">
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                          <Stat title={"Amount"} value={details?.total_amount ?? "-"} />
                          <Stat title={"Date"} value={details?.created_at ?? "-"} />
                          <Stat title={"Invoice no"} value={details?.invoice_no ?? "-"} />
                          <Stat title={"Notes"} value={details?.notes ?? "-"} />
                          {/* <Stat title={"CPL"} value={details?.summary?.cpl ?? "-"} />
                          <Stat title={"Total Clicks"} value={details?.summary?.total_clicks ?? "-"} />
                          <Stat title={"Total Impressions"} value={details?.summary?.total_impressions ?? "-"} />
                          <Stat title={"CTR"} value={details?.summary?.ctr ?? "-"} />
                          <Stat title={"Average CPC"} value={details?.summary?.avg_cpc ?? "-"} /> */}
                        </div>

                        <div className="min-h-0 overflow-auto rounded-xl w-full pb-2 relative">
                          {
                            details?.tankers?.length ?
                            <div className="grid grid-cols-1 gap-3">
                              <div className={`sticky top-0 grid grid-cols-4 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="flex items-center gap-2 text-[13px] font-medium text-slate-800 capitalize">
                                  Date
                                </div>

                                <div className="flex items-center gap-2 text-[13px] font-medium text-slate-800 capitalize">
                                  Gallon
                                </div>

                                <div className="flex items-center gap-2 text-[13px] font-medium text-slate-800 capitalize">
                                  Tanker no
                                </div>

                                <div className="flex items-center gap-2 text-[13px] font-medium text-slate-800 capitalize">
                                  Amount
                                </div>
                                
                              </div>
                              {details?.tankers?.map((eachList, i) => (
                                <div key={i} className={`grid grid-cols-4 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                  <div className="flex gap-2 text-[13px] font-medium text-slate-500">
                                    <span>{eachList?.date || "-"}</span>
                                  </div>

                                  <div className="flex gap-2 text-[13px] font-medium text-slate-500">
                                    <span>{eachList?.quantity || "-"}</span>
                                  </div>

                                  <div className="flex gap-2 text-[13px] font-medium text-slate-500">
                                    <span>{eachList?.tanker_no || "-"}</span>
                                  </div>

                                  <div className="flex gap-2 text-[13px] font-medium text-slate-500">
                                    <span>{eachList?.amount || "-"}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            :
                            <DataNotFound title={"Tankers Details"} />
                          }
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <InnerLoader wrapperClass={'w-full'} />
                )
              }
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
      {showModal &&
        <UpdateInvoice onClose={() => {setShowModal(false)}} onError={onError} onSubmit={onSuccess} invoiceId={invoiceId} />
      }
      <Snackbar open={alertData?.isShow} autoHideDuration={3000} onClose={handleClose}>
        <Alert
          onClose={handleClose}
          severity={alertData?.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alertData?.message}
        </Alert>
      </Snackbar>
    </>
  );
}